/**
 * Hand-authored stroke icons. No icon library, no icon font, no emoji.
 *
 * Every glyph is drawn on a 16×16 grid with `stroke="currentColor"` so it inherits the
 * surrounding text colour and participates in hover transitions for free.
 */

import type { ReactElement } from 'react';

export type IconName =
  | 'today'
  | 'calendar'
  | 'list'
  | 'chores'
  | 'settings'
  | 'plus'
  | 'minus'
  | 'close'
  | 'check'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-down'
  | 'repeat'
  | 'link'
  | 'pin'
  | 'trash'
  | 'mic'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'refresh'
  | 'alert'
  | 'cake'
  | 'copy'
  | 'drag'
  | 'image'
  | 'upload'
  | 'note'
  | 'cart'
  | 'cloud'
  | 'cloud-sun'
  | 'rain'
  | 'snow'
  | 'fog'
  | 'storm'
  | 'briefcase'
  | 'school'
  | 'activity'
  | 'medical'
  | 'people'
  | 'home'
  | 'tag'
  | 'camera'
  | 'meal'
  | 'message'
  | 'timer'
  | 'gift'
  | 'bell'
  | 'plane'
  | 'star'
  | 'flag'
  | 'heart'
  | 'sparkle'
  | 'golf'
  | 'map'
  | 'lock'
  | 'shield'
  | 'key'
  | 'eye'
  | 'eye-off'
  | 'theme-auto';

const PATHS: Record<IconName, ReactElement> = {
  today: (
    <>
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2" />
    </>
  ),
  calendar: (
    <>
      <rect x="2" y="3.5" width="12" height="10.5" rx="1.5" />
      <path d="M2 6.5h12M5.5 2v3M10.5 2v3" />
    </>
  ),
  list: <path d="M5.5 4h8.5M5.5 8h8.5M5.5 12h8.5M2.5 4h.01M2.5 8h.01M2.5 12h.01" />,
  chores: (
    <>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
      <path d="M5.5 8.2l1.8 1.8 3.4-3.6" />
    </>
  ),
  settings: (
    <>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.8v1.6M8 12.6v1.6M14.2 8h-1.6M3.4 8H1.8M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1M12.4 12.4l-1.1-1.1M4.7 4.7L3.6 3.6" />
    </>
  ),
  plus: <path d="M8 3.2v9.6M3.2 8h9.6" />,
  minus: <path d="M3.2 8h9.6" />,
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  check: <path d="M3.5 8.5l3 3 6-7" />,
  'chevron-left': <path d="M10 3.5L5.5 8l4.5 4.5" />,
  'chevron-right': <path d="M6 3.5L10.5 8 6 12.5" />,
  'chevron-down': <path d="M3.5 6L8 10.5 12.5 6" />,
  repeat: (
    <>
      <path d="M3 8a5 5 0 0 1 5-5 5 5 0 0 1 4.3 2.5M13 8a5 5 0 0 1-5 5 5 5 0 0 1-4.3-2.5" />
      <path d="M12.5 2.5v3h-3M3.5 13.5v-3h3" />
    </>
  ),
  link: (
    <>
      <path d="M6.5 9.5a2.5 2.5 0 0 0 3.5 0l2-2a2.5 2.5 0 0 0-3.5-3.5l-.6.6" />
      <path d="M9.5 6.5a2.5 2.5 0 0 0-3.5 0l-2 2a2.5 2.5 0 0 0 3.5 3.5l.6-.6" />
    </>
  ),
  pin: <path d="M6 2h4M8 2v5M4.5 7h7l-1 3h-5zM8 10v4" />,
  trash: (
    <>
      <path d="M2.5 4.5h11M6 4.5V3h4v1.5M4 4.5l.6 8.5a1 1 0 0 0 1 1h4.8a1 1 0 0 0 1-1L12 4.5" />
      <path d="M6.7 7v4M9.3 7v4" />
    </>
  ),
  mic: (
    <>
      <rect x="6" y="1.8" width="4" height="7.5" rx="2" />
      <path d="M3.8 7.5a4.2 4.2 0 0 0 8.4 0M8 11.7v2.5" />
    </>
  ),
  sun: (
    <>
      <circle cx="8" cy="8" r="2.6" />
      <path d="M8 1.5v1.4M8 13.1v1.4M14.5 8h-1.4M2.9 8H1.5M12.6 3.4l-1 1M4.4 11.6l-1 1M12.6 12.6l-1-1M4.4 4.4l-1-1" />
    </>
  ),
  moon: <path d="M13 9.4A5.5 5.5 0 0 1 6.6 3a5.5 5.5 0 1 0 6.4 6.4z" />,
  monitor: (
    <>
      <rect x="2" y="3" width="12" height="8.5" rx="1.5" />
      <path d="M5.5 14h5M8 11.5V14" />
    </>
  ),
  refresh: (
    <>
      <path d="M13.2 7.2A5.2 5.2 0 0 0 4 4.8M2.8 8.8A5.2 5.2 0 0 0 12 11.2" />
      <path d="M13.5 3.5v3.5H10M2.5 12.5V9H6" />
    </>
  ),
  alert: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.8v3.6M8 11h.01" />
    </>
  ),
  cake: (
    <>
      <path d="M2.5 13.5h11M3.5 13.5V8.5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v5" />
      <path d="M8 7.5V5M8 2.5v.8" />
    </>
  ),
  copy: (
    <>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
    </>
  ),
  drag: <path d="M6 4h.01M10 4h.01M6 8h.01M10 8h.01M6 12h.01M10 12h.01" />,
  image: (
    <>
      <rect x="2" y="3" width="12" height="10" rx="1.5" />
      <circle cx="5.8" cy="6.3" r="1" />
      <path d="M2.5 11l3-2.8 2.4 2.2 2.4-2.4 3.2 3" />
    </>
  ),
  upload: <path d="M8 11V2.8M4.8 6L8 2.8 11.2 6M2.5 10.5v2a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-2" />,
  note: (
    <>
      <path d="M3 2.5h7L13 5.5v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1z" />
      <path d="M9.5 2.5v3.5H13M5.5 9h5M5.5 11.5h3" />
    </>
  ),
  cart: (
    <>
      <circle cx="6" cy="13" r="1" />
      <circle cx="11.5" cy="13" r="1" />
      <path d="M1.5 2.5h1.8l1.6 7.2h7.4l1.2-5.2H4" />
    </>
  ),
  cloud: <path d="M4.6 12.5a2.8 2.8 0 0 1-.3-5.6 3.8 3.8 0 0 1 7.3-.8 2.9 2.9 0 0 1-.4 6.4z" />,
  'cloud-sun': (
    <>
      <path d="M5.6 5.2a2.4 2.4 0 0 1 4-1.1M3.4 2.6l.7.7M2 6h1M4.1 9.4l.6-.6" />
      <path d="M6 13.5a2.6 2.6 0 0 1-.3-5.2 3.5 3.5 0 0 1 6.8-.7 2.7 2.7 0 0 1-.4 5.9z" />
    </>
  ),
  rain: (
    <>
      <path d="M4.8 9.8a2.6 2.6 0 0 1-.3-5.2 3.5 3.5 0 0 1 6.8-.7 2.7 2.7 0 0 1-.4 5.9" />
      <path d="M5.5 11.5l-.8 2M8 11.5l-.8 2M10.5 11.5l-.8 2" />
    </>
  ),
  snow: (
    <>
      <path d="M4.8 9.3a2.6 2.6 0 0 1-.3-5.2 3.5 3.5 0 0 1 6.8-.7 2.7 2.7 0 0 1-.4 5.9" />
      <path d="M5.5 12h.01M8 13.2h.01M10.5 12h.01M8 11h.01" />
    </>
  ),
  fog: (
    <>
      <path d="M4.8 8.3a2.6 2.6 0 0 1-.3-5.2 3.5 3.5 0 0 1 6.8-.7 2.7 2.7 0 0 1-.4 5.9" />
      <path d="M3 10.8h10M4.5 13.2h7" />
    </>
  ),
  storm: (
    <>
      <path d="M4.8 9.3a2.6 2.6 0 0 1-.3-5.2 3.5 3.5 0 0 1 6.8-.7 2.7 2.7 0 0 1-.4 5.9" />
      <path d="M8.6 9.5L6.8 12h2.4l-1.8 2.5" />
    </>
  ),
  briefcase: (
    <>
      <rect x="2" y="5" width="12" height="8.5" rx="1.5" />
      <path d="M5.5 5V3.8a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V5M2 8.5h12" />
    </>
  ),
  school: <path d="M8 2.5L14.5 6 8 9.5 1.5 6zM4 7.5v3.2c0 .8 1.8 1.8 4 1.8s4-1 4-1.8V7.5" />,
  activity: <path d="M1.5 8h3l2 4.5 3-9 2 4.5h3" />,
  medical: (
    <>
      <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
      <path d="M8 5v6M5 8h6" />
    </>
  ),
  people: (
    <>
      <circle cx="6" cy="6" r="2.2" />
      <path d="M2.5 13c0-2 1.6-3.4 3.5-3.4S9.5 11 9.5 13" />
      <path d="M10.5 4.2a2.2 2.2 0 0 1 0 4.2M11 9.7c1.6.2 2.7 1.5 2.7 3.3" />
    </>
  ),
  home: (
    <>
      <path d="M2.5 7.5L8 3l5.5 4.5" />
      <path d="M3.7 6.6v6.4a.5.5 0 0 0 .5.5h7.6a.5.5 0 0 0 .5-.5V6.6" />
      <path d="M6.5 13.5v-3.6a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v3.6" />
    </>
  ),
  tag: (
    <>
      <path d="M2.5 2.5h4.2l6.3 6.3a1 1 0 0 1 0 1.4l-2.8 2.8a1 1 0 0 1-1.4 0L2.5 6.7z" />
      <circle cx="5.2" cy="5.2" r="0.9" />
    </>
  ),
  camera: (
    <>
      <path d="M2.5 5h2l1-1.5h5L11.5 5h2a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
      <circle cx="8" cy="9" r="2.3" />
    </>
  ),
  meal: (
    <>
      <path d="M4 2v5a1.5 1.5 0 0 0 3 0V2M5.5 2v12" />
      <path d="M11 2c-1.2 0-2 1.4-2 3.2 0 1.5.7 2.5 1.6 2.7L10.5 14" />
    </>
  ),
  message: <path d="M2.5 3.5h11a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H6l-3 2.5V11.5H2.5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z" />,
  timer: (
    <>
      <circle cx="8" cy="9" r="5.2" />
      <path d="M8 9V5.8M6.2 1.8h3.6" />
    </>
  ),
  gift: (
    <>
      <rect x="2.5" y="6.5" width="11" height="7.5" rx="1" />
      <path d="M1.8 6.5h12.4v2.2H1.8zM8 6.5V14" />
      <path d="M8 6.5S7 3.5 5.4 3.5a1.6 1.6 0 0 0 0 3zM8 6.5s1-3 2.6-3a1.6 1.6 0 0 1 0 3z" />
    </>
  ),
  bell: (
    <>
      <path d="M8 2.2a4 4 0 0 0-4 4c0 3.5-1.2 4.6-1.2 4.6h10.4S12 9.7 12 6.2a4 4 0 0 0-4-4z" />
      <path d="M6.8 13a1.3 1.3 0 0 0 2.4 0" />
    </>
  ),
  plane: <path d="M8 1.5c.7 0 1.1 1 1.1 2.3v2.4l4.4 2.6v1.5l-4.4-1.3v2.6l1.4 1v1.1L8 13.6l-2.5.7v-1.1l1.4-1V9.6L2.5 10.9V9.4l4.4-2.6V4.4C6.9 2.5 7.3 1.5 8 1.5z" />,
  star: <path d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />,
  flag: (
    <>
      <path d="M3.5 14V2.5" />
      <path d="M3.5 3h8l-1.6 2.6L11.5 8h-8z" />
    </>
  ),
  heart: <path d="M8 13.5S2.5 10 2.5 6.2A2.7 2.7 0 0 1 8 5a2.7 2.7 0 0 1 5.5 1.2C13.5 10 8 13.5 8 13.5z" />,
  sparkle: <path d="M8 1.8l1.1 3.6 3.6 1.1-3.6 1.1L8 11.2 6.9 7.6 3.3 6.5l3.6-1.1z" />,
  golf: (
    <>
      <path d="M6 14V2.5l5 2.2-5 2.3" />
      <circle cx="4.6" cy="14" r="1" />
    </>
  ),
  map: (
    <>
      <path d="M8 14s4.5-4 4.5-7.5a4.5 4.5 0 0 0-9 0C3.5 10 8 14 8 14z" />
      <circle cx="8" cy="6.4" r="1.7" />
    </>
  ),
  lock: (
    <>
      <rect x="3.3" y="7" width="9.4" height="6.7" rx="1.4" />
      <path d="M5.4 7V5.1a2.6 2.6 0 0 1 5.2 0V7" />
    </>
  ),
  shield: <path d="M8 2.1l4.6 1.8v3.4c0 3-1.9 5.1-4.6 6.1-2.7-1-4.6-3.1-4.6-6.1V3.9z" />,
  key: (
    <>
      <circle cx="5.6" cy="10.4" r="2.6" />
      <path d="M7.4 8.6L13 3M11 5l1.4 1.4M9.6 6.4L11 7.8" />
    </>
  ),
  eye: (
    <>
      <path d="M1.7 8S4 3.9 8 3.9 14.3 8 14.3 8 12 12.1 8 12.1 1.7 8 1.7 8z" />
      <circle cx="8" cy="8" r="1.9" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M6.3 4.2A6.7 6.7 0 0 1 8 3.9c4 0 6.3 4.1 6.3 4.1a11.6 11.6 0 0 1-1.9 2.3M9.7 9.7A1.9 1.9 0 0 1 6.3 6.4M1.7 8s1.1-2 3-3.2" />
      <path d="M2.5 2.5l11 11" />
    </>
  ),
  'theme-auto': (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 2a6 6 0 0 1 0 12z" fill="currentColor" stroke="none" />
    </>
  ),
};

/**
 * WMO weather code → icon. `isDay` only changes the clear-sky glyph; a rain cloud looks
 * the same at midnight.
 */
export function weatherIcon(code: number, isDay = true): IconName {
  if (code === 0) return isDay ? 'sun' : 'moon';
  if (code === 1 || code === 2) return 'cloud-sun';
  if (code === 3) return 'cloud';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'rain';
  if (code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'storm';
  return 'cloud';
}

export function Icon({
  name,
  size = 16,
  className,
  strokeWidth = 1.4,
  style,
}: {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
